import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import drHarris from "@/assets/restore/dr-harris.jpg.asset.json";
import drCloninger from "@/assets/restore/dr-cloninger.jpg.asset.json";
import drLovitt from "@/assets/restore/dr-lovitt.jpg.asset.json";
import drJuboori from "@/assets/restore/dr-juboori.png.asset.json";
import drSaleh from "@/assets/restore/dr-saleh.jpg.asset.json";
import drPratt from "@/assets/restore/dr-pratt.jpg.asset.json";
const drPatel = "/images/dr-anish-patel-directory.jpg";

const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

export const Route = createFileRoute("/doctors/")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/doctors/" }],
    meta: [
      { title: "RESTORE® Hair Doctors — Elite Hair Restoration Team | RESTORE® Hair" },
      {
        name: "description",
        content:
          "Meet the world-class RESTORE® physicians — hand-selected, trained and mentored by Medical Director Dr. James Harris, inventor of the Harris S.A.F.E. System.",
      },
      { property: "og:title", content: "RESTORE® Hair Doctors — Elite Hair Restoration Team" },
      {
        property: "og:description",
        content:
          "Our elite team of RESTORE® physicians has dedicated their careers to hair restoration — and nothing else.",
      },
    ],
  }),
  component: DoctorsPage,
});

type Doctor = {
  name: string;
  title: string;
  image: string;
  href: string;
  alt: string;
  cropToPortrait?: boolean;
};

const DOCTORS: Doctor[] = [
  {
    name: "Dr. James Harris",
    title: "Medical Director & Hair Restoration Physician",
    image: drHarris.url,
    href: "/doctors/dr-james-harris/",
    alt: "Dr. James Harris — RESTORE Hair Medical Director",
  },
  {
    name: "Dr. Adam Cloninger",
    title: "Hair Restoration Physician",
    image: drCloninger.url,
    href: "/doctors/dr-adam-cloninger/",
    alt: "Dr. Adam Cloninger — RESTORE Hair Physician",
  },
  {
    name: "Dr. Shelly Lovitt",
    title: "Hair Restoration Physician",
    image: drLovitt.url,
    href: "/doctors/dr-shelly-lovitt/",
    alt: "Dr. Shelly Lovitt — RESTORE Hair Physician",
  },
  {
    name: "Dr. Fahad Juboori",
    title: "Hair Restoration Physician",
    image: drJuboori.url,
    href: "/doctors/dr-fahad-juboori/",
    alt: "Dr. Fahad Juboori — RESTORE Hair Physician",
    cropToPortrait: true,
  },
  {
    name: "Dr. Shadia Saleh",
    title: "Hair Restoration Physician",
    image: drSaleh.url,
    href: "/doctors/dr-shadia-saleh/",
    alt: "Dr. Shadia Saleh — RESTORE Hair Physician",
  },
  {
    name: "Dr. Broc Pratt",
    title: "Hair Restoration Physician",
    image: drPratt.url,
    href: "/doctors/dr-broc-pratt/",
    alt: "Dr. Broc Pratt — RESTORE Hair Physician",
    cropToPortrait: true,
  },
  {
    name: "Dr. Anish Patel",
    title: "Hair Restoration Physician",
    image: drPatel,
    href: "/doctors/dr-anish-patel/",
    alt: "Dr. Anish Patel — RESTORE Hair Physician",
  },
];

function DoctorsPage() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main data-cta-location="doctor">
        <section className="bg-background py-12 md:py-20">
          <div className="mx-auto max-w-6xl px-4">
            <header className="mx-auto max-w-3xl text-center">
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
                RESTORE® Hair Doctors
              </h1>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground">
                Our world class team of RESTORE® Doctors and hair physicians have
                dedicated their careers to hair restoration — and nothing else. Each physician
                has been hand selected, trained and mentored by RESTORE's Medical Director,
                Dr. James Harris; an award winning hair surgeon, renowned author, international
                public speaker, and the inventor of the 'Harris S.A.F.E System', which is used
                to perform FUE by doctors throughout the world.
              </p>
            </header>

            <ul className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {DOCTORS.map((doc) => (
                <li
                  key={doc.name}
                  className="flex h-full flex-col border border-border bg-card"
                >
                  <div className="aspect-[3/4] w-full overflow-hidden bg-muted">
                    <img
                      src={doc.image}
                      alt={doc.alt}
                      loading="lazy"
                      className={`h-full w-full object-top ${doc.cropToPortrait ? "object-cover" : "object-contain"}`}
                    />
                  </div>
                  <div className="flex min-h-[160px] flex-col px-5 pb-6 pt-5">
                    <h2 className="text-lg md:text-xl font-semibold text-foreground">
                      {doc.name}
                    </h2>
                    <p className="mt-1 text-sm italic text-muted-foreground">
                      {doc.title}
                    </p>
                    <a
                      href={doc.href}
                      className="mt-4 self-start text-sm font-semibold text-primary underline-offset-4 hover:underline"
                    >
                      Read Bio
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <Suspense fallback={null}>
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}